import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { retriageTasks, approveTriage } from '../../../utils/triageService';
import {
	triageDiff,
	proposedSubtasks,
	isNeedsInfo,
	isApproved,
	canApprove,
	approvalBlockReason,
	changeCount,
	hasProposal,
	PRIORITY_LABEL,
	FILL,
	AGREE,
	OVERWRITE,
	KEPT,
} from './triageProposal';
import { areaMeta } from './taskModel';

// Auto-triage proposal panel (Slice 1 — READ ONLY).
// ---------------------------------------------------------------------------
// `triageWorkCapture` has been classifying raw captures since 2026-07-11 and
// nothing has ever rendered its output. This is that surface: it shows what the
// classifier decided and, field by field, exactly what approving it WOULD do —
// deliberately before any write path exists, so the judgment can be assessed on
// real proposals before it is trusted to commit to them.
//
// SLICE 2 (2026-07-25) added the commit path — the `approveTriage` callable —
// and it reads the same `triageDiff` rows this renders, so the surface and the
// write can never disagree about what "approve" means. Two details worth keeping:
//
//   * Approve is a TWO-TAP inline confirm, not a dialog. The consequence is
//     plural and partly invisible (N fields changed AND M new task documents
//     created), and the second tap states both counts. A dialog on a phone would
//     cover the very diff the person is deciding about.
//   * An approved payload is kept, not deleted, so this panel needs a terminal
//     state — otherwise the surface would keep offering to approve what it just
//     committed.

const VERDICT_META = {
	[FILL]: { label: 'fills a blank', icon: 'fa-plus' },
	[AGREE]: { label: 'already matches', icon: 'fa-equals' },
	[OVERWRITE]: { label: 'would change', icon: 'fa-arrow-right-arrow-left' },
	[KEPT]: { label: 'kept — you set this', icon: 'fa-lock' },
};

function ConfidenceChip({ confidence }) {
	if (!confidence) return null;
	return (
		<span className={`wt-triage__conf wt-triage__conf--${confidence}`}>
			{confidence} confidence
		</span>
	);
}

function DiffRow({ row }) {
	const meta = VERDICT_META[row.verdict] || VERDICT_META[AGREE];
	return (
		<div className={`wt-triage__row wt-triage__row--${row.verdict}`}>
			<span className="wt-triage__field">{row.label}</span>
			<span className="wt-triage__change">
				<span className="wt-triage__from">{row.from}</span>
				<i className="fa-sharp fa-solid fa-arrow-right wt-triage__arrow" />
				<span className="wt-triage__to">{row.to}</span>
			</span>
			<span className="wt-triage__verdict">
				<i className={`fa-sharp fa-solid ${meta.icon}`} />
				{meta.label}
			</span>
		</div>
	);
}

function ProposedTriagePanel({ task }) {
	const { userProfile } = useAuth();
	const [rerunning, setRerunning] = useState(false);
	const [rerunError, setRerunError] = useState(null);
	const [confirming, setConfirming] = useState(false);
	const [approving, setApproving] = useState(false);
	const [approveError, setApproveError] = useState(null);

	if (!hasProposal(task)) return null;

	const p = task.proposedTriage;
	const rows = triageDiff(task);
	const subs = proposedSubtasks(task);
	const needsInfo = isNeedsInfo(task);
	const kept = rows.find((r) => r.verdict === KEPT);
	const approved = isApproved(task);
	const nChanges = changeCount(task);
	// Delegated to the shared predicate rather than re-derived here, so the
	// button is offered exactly when the callable would accept it.
	const approvable = canApprove(task);
	const blockedBecause = approvalBlockReason(task);
	// Re-running spends API budget, so it matches the callable's own gate.
	const canRerun = userProfile?.role === 'admin' || userProfile?.owner === true;

	const rerun = async () => {
		if (rerunning) return;
		setRerunning(true);
		setRerunError(null);
		try {
			// The live task subscription re-renders this panel with the new
			// payload — nothing to set locally.
			await retriageTasks(task.id);
		} catch (e) {
			setRerunError(e?.message || 'Re-run failed');
		}
		setRerunning(false);
	};

	// withSubtasks:false is the "fields only" escape hatch — offered only when
	// there are sub-tasks to decline, so the common case stays one decision.
	const approve = async (withSubtasks) => {
		if (approving) return;
		setApproving(true);
		setApproveError(null);
		try {
			// The live task subscription re-renders this panel in its approved
			// state; nothing to set locally.
			await approveTriage(task.id, { withSubtasks });
			setConfirming(false);
		} catch (e) {
			setApproveError(e?.message || 'Approve failed');
		}
		setApproving(false);
	};

	return (
		<div
			className={`wt-triage${needsInfo ? ' wt-triage--needsinfo' : ''}${
				approved ? ' wt-triage--approved' : ''
			}`}>
			<div className="wt-triage__head">
				<i className="fa-sharp fa-solid fa-wand-magic-sparkles" />
				<span className="wt-triage__title">
					{needsInfo
						? 'Auto-triage needs more information'
						: approved
							? 'Auto-triage approved'
							: 'Auto-triage proposal'}
				</span>
				<ConfidenceChip confidence={p.confidence} />
			</div>

			{needsInfo ? (
				<p className="wt-triage__note">
					{p.needsInfoNote ||
						'The classifier declined to guess and left this for a human.'}
				</p>
			) : (
				<>
					<div className="wt-triage__rows">
						{rows.map((r) => (
							<DiffRow key={r.field} row={r} />
						))}
					</div>

					{kept && (
						<p className="wt-triage__carveout">
							<i className="fa-sharp fa-solid fa-shield-check" />
							Priority stays <strong>{kept.from}</strong> — you chose it, so
							{approved ? ' approving did' : ' approving will'} not change it.
						</p>
					)}

					{subs.length > 0 && (
						<div className="wt-triage__subs">
							<div className="wt-triage__subs-title">
								Proposed sub-tasks
								<span className="wt-triage__count">{subs.length}</span>
							</div>
							{subs.map((s, i) => (
								<div key={i} className="wt-triage__sub">
									<i className="fa-sharp fa-solid fa-list-tree" />
									<span className="wt-triage__sub-title">{s.title}</span>
									{s.area && (
										<span className="wt-triage__sub-meta">{areaMeta(s.area).label}</span>
									)}
									{s.priority && (
										<span className="wt-triage__sub-meta">
											{PRIORITY_LABEL[s.priority] || s.priority}
										</span>
									)}
								</div>
							))}
						</div>
					)}
				</>
			)}

			{p.rationale && <p className="wt-triage__why">{p.rationale}</p>}

			{(rerunError || approveError) && (
				<p className="wt-triage__error">
					<i className="fa-sharp fa-solid fa-triangle-exclamation" />
					{rerunError || approveError}
				</p>
			)}

			{approvable && (
				<div className="wt-triage__actions">
					{!confirming ? (
						<button
							type="button"
							className="btn-cyv-teal wt-triage__approve"
							onClick={() => setConfirming(true)}>
							<i className="fa-sharp fa-solid fa-check" />
							Approve
						</button>
					) : (
						<>
							{/* The second tap states BOTH consequences. Sub-task creation is
							    the invisible half — it writes new documents, not just
							    fields — so it is named before it happens, never after. */}
							<p className="wt-triage__confirm">
								Apply {nChanges} change{nChanges === 1 ? '' : 's'}
								{subs.length > 0
									? ` and create ${subs.length} sub-task${subs.length === 1 ? '' : 's'}?`
									: '?'}
							</p>
							<div className="wt-triage__confirmrow">
								<button
									type="button"
									className="btn-cyv-teal wt-triage__approve"
									disabled={approving}
									onClick={() => approve(true)}>
									<i
										className={`fa-sharp fa-solid ${
											approving ? 'fa-spinner fa-spin' : 'fa-check'
										}`}
									/>
									{approving ? 'Approving…' : 'Yes, approve'}
								</button>
								{subs.length > 0 && (
									<button
										type="button"
										className="wt-triage__fieldsonly"
										disabled={approving}
										onClick={() => approve(false)}>
										Fields only
									</button>
								)}
								<button
									type="button"
									className="wt-triage__cancel"
									disabled={approving}
									onClick={() => setConfirming(false)}>
									Cancel
								</button>
							</div>
						</>
					)}
				</div>
			)}

			<div className="wt-triage__foot">
				<span>
					{approved
						? `Approved${
							Array.isArray(p.approvedFields) && p.approvedFields.length
								? ` — applied ${p.approvedFields.join(', ')}`
								: ' — no fields needed changing'
						}`
						: blockedBecause === 'already-triaged'
							? 'Already triaged — approving is off to avoid overwriting what you set.'
							: needsInfo
								? 'Nothing to commit — fill in what is missing, or re-run.'
								: 'Nothing is committed until you approve.'}
				</span>
				{canRerun && !approved && (
					<button
						type="button"
						className="wt-triage__rerun"
						disabled={rerunning}
						onClick={rerun}>
						<i
							className={`fa-sharp fa-solid ${
								rerunning ? 'fa-spinner fa-spin' : 'fa-rotate'
							}`}
						/>
						{rerunning ? 'Re-running…' : 'Re-run triage'}
					</button>
				)}
				{p.model && <span className="wt-triage__model">{p.model}</span>}
			</div>
		</div>
	);
}

export default ProposedTriagePanel;
